"use client";
import Image from "next/image";
import Icon from "@/components/icons";
import type { Provider } from "@/lib/auth/getThirdPartyAuthProviders";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
  LogistrationElementName,
  LogistrationElementText,
} from "@/constants/tagular/main";
import { useInView } from "react-intersection-observer";
import { useEffect } from "react";
import appleIcon from "@/public/icons/apple.svg";
import googleIcon from "@/public/icons/google.svg";
import facebookIcon from "@/public/icons/fb.svg";
import microsoftIcon from "@/public/icons/microsoft.svg";

const providerButtonClass: Record<string, string> = {
  "oa2-facebook":
    "bg-[#1e40af] border border-[#1e40af] text-white hover:bg-[#1e3a8a] hover:border-[#1e3a8a] hover:text-white focus:bg-[#1e3a8a] focus:border-[#1e3a8a] focus:text-white",
  "oa2-google-oauth2":
    "bg-[#187dae] border border-[#187dae] text-white hover:bg-[#1565c0] hover:border-[#1565c0] hover:text-white focus:bg-[#1565c0] focus:border-[#1565c0] focus:text-white",
  "oa2-apple-id":
    "bg-black border border-black text-white text-[16px] hover:bg-black hover:border-black hover:text-white focus:bg-black focus:border-black focus:text-white",
  "oa2-azuread-oauth2":
    "bg-[#2f2f2f] border border-[#2f2f2f] text-white hover:bg-black hover:border-black hover:text-white focus:bg-black focus:border-black focus:text-white",
};

const providerIconClass: Record<string, string> = {
  "oa2-google-oauth2":
    "h-[24px] max-h-[24px] max-w-[21px] ltr:ml-[-6px] rtl:mr-[-6px] mr-2",
  "oa2-apple-id": "max-h-[1.8em] max-w-[2em] mr-2",
  // Default for other providers
  default: "mr-2",
};

type TpaButtonProps = {
  provider: Provider;
  isRegistration: boolean;
  baseUrl: string;
  index: number;
  isLogistrationV1?: boolean;
};

export default function TpaButton({
  provider,
  isRegistration,
  baseUrl,
  index,
  isLogistrationV1,
}: TpaButtonProps) {
  const providerUrl = isRegistration ? provider.registerUrl : provider.loginUrl;
  const buttonClass =
    providerButtonClass[provider.id || ""] ||
    "flex items-center px-1.5 py-1 rounded border border-gray-300 bg-white shadow hover:bg-gray-50 transition-colors min-w-[180px]";

  const { elementClicked, elementViewed } = useElementEvents({
    webElement: {
      elementType: ElementType.Button,
      text: isRegistration
        ? `${LogistrationElementText.CreateAccount} with ${provider.name}`
        : `${LogistrationElementText.SignIn} with ${provider.name}`,
      name: isRegistration
        ? LogistrationElementName.CreateAccount
        : LogistrationElementName.SignIn,
    },
  });

  const { ref, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  useEffect(() => {
    if (inView && !isLogistrationV1) {
      elementViewed();
    }
  }, [inView, elementViewed, isLogistrationV1]);

  if (isLogistrationV1) {
    return (
      <TpaButtonLogistrationV1
        provider={provider}
        ref={ref}
        providerUrl={providerUrl || ""}
        baseUrl={baseUrl}
        elementClicked={elementClicked}
      />
    );
  }

  return (
    <button
      type="button"
      key={provider.id}
      ref={ref}
      className={`flex max-sm:w-full items-center px-1.5 py-1.5 rounded font-semibold transition-colors min-w-[224px] ${buttonClass}`}
      onClick={() => {
        elementClicked();
        if (providerUrl) {
          window.location.href = baseUrl + providerUrl;
        }
      }}
    >
      {provider.iconImage ? (
        <Image
          src={provider.iconImage}
          alt={`icon ${provider.name}`}
          width={21}
          height={24}
          className={
            providerIconClass[provider.id || ""] || providerIconClass.default
          }
        />
      ) : (
        <Icon
          type="launch"
          svgProps={{
            width: 21,
            height: 24,
            className:
              providerIconClass[provider.id || ""] || providerIconClass.default,
          }}
        />
      )}
      <span className="font-normal">{provider.name}</span>
    </button>
  );
}

type TpaButtonLogistrationV1Props = {
  provider: Provider;
  ref: (node?: Element | null) => void;
  providerUrl: string;
  baseUrl: string;
  elementClicked: () => void;
};

const TpaButtonLogistrationV1 = ({
  provider,
  ref,
  providerUrl,
  baseUrl,
  elementClicked,
}: TpaButtonLogistrationV1Props) => {
  const iconMap = {
    "oa2-apple-id": appleIcon,
    "oa2-google-oauth2": googleIcon,
    "oa2-facebook": facebookIcon,
    "oa2-azuread-oauth2": microsoftIcon,
  };

  return (
    <button
      type="button"
      key={provider.id}
      ref={ref}
      className={`flex items-center justify-center w-[67px] md:w-[86px] h-[67px] md:h-[86px] rounded-xl bg-white border border-putty-700`}
      onClick={() => {
        elementClicked();
        if (providerUrl) {
          window.location.href = baseUrl + providerUrl;
        }
      }}
    >
      {provider.iconImage ? (
        <Image
          src={
            iconMap[provider.id as keyof typeof iconMap] || provider.iconImage
          }
          className="w-[40px] h-[40px] md:w-[40px] md:h-[47px]"
          alt={`icon ${provider.name}`}
        />
      ) : (
        <Icon
          type="launch"
          svgProps={{
            width: 21,
            height: 24,
          }}
        />
      )}
    </button>
  );
};
